/**
 * Admin Context Selectors
 *
 * Memoized selectors for reading admin context state.
 * Used by: components, copilot, API interceptors
 *
 * Note: Selectors handle undefined state gracefully for portals
 * that don't have adminContext reducer registered.
 */

import { createSelector } from '@reduxjs/toolkit'
import type { AdminContextState } from '../domain/types/admin-context.types'

// Root state type - adminContext may be undefined in some portals
interface RootState {
  adminContext?: AdminContextState
}

// Default state for when adminContext is not registered
const DEFAULT_STATE: AdminContextState = {
  isAdmin: false,
  isLoading: false,
  selectedTenantId: null,
  selectedTenant: null,
  tenantsList: [],
}

/**
 * Base selector for admin context state
 * Returns default state if adminContext is not registered in the store
 */
export const selectAdminContext = (state: RootState): AdminContextState =>
  state.adminContext ?? DEFAULT_STATE

/**
 * Select whether current user is an admin
 */
export const selectIsAdmin = createSelector(
  selectAdminContext,
  (ctx) => ctx.isAdmin
)

/**
 * Select currently selected tenant (full object)
 */
export const selectSelectedTenant = createSelector(
  selectAdminContext,
  (ctx) => ctx.selectedTenant
)

/**
 * Select currently selected tenant ID
 */
export const selectSelectedTenantId = createSelector(
  selectAdminContext,
  (ctx) => ctx.selectedTenantId
)

/**
 * Select list of available tenants
 */
export const selectTenantsList = createSelector(
  selectAdminContext,
  (ctx) => ctx.tenantsList
)

/**
 * Select tenant ID for API calls (used in X-Tenant-Id header)
 * Returns null if "All Tenants" is selected
 */
export const selectTenantContext = createSelector(
  selectAdminContext,
  (ctx) => ctx.selectedTenantId
)

/**
 * Select loading state
 */
export const selectIsAdminLoading = createSelector(
  selectAdminContext,
  (ctx) => ctx.isLoading
)

/**
 * Check if admin has selected a specific tenant
 */
export const selectHasTenantSelected = createSelector(
  selectAdminContext,
  (ctx) => ctx.selectedTenant !== null
)

/**
 * Get tenant display name (or "All Tenants" if none selected)
 */
export const selectTenantDisplayName = createSelector(
  selectAdminContext,
  (ctx) => ctx.selectedTenant?.name ?? 'All Tenants'
)
